import { Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { ArrowPathIcon, BanknotesIcon, GiftIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { collection, getDocs } from 'firebase/firestore';

import PaginationControls from '@/components/shared/PaginationControls/PaginationControls';
import { BaseModal } from '@/components/shared/BaseModal';
import { db } from '@/config/firebase';
import { consolidateExpensesByCompetence, removeExpensesConsolidation } from '../services/expensesConsolidationService';
import { useBenefitCompetenceTotals } from '../hooks/useBenefitCompetenceTotals';
import { usePayrollCompetenceTotals } from '../hooks/usePayrollCompetenceTotals';
import { benefitTypes } from './benefitTypes';



type OverviewTotalsRow = {
  competence: string;
  sortKey: string | null;
  payrollTotal: number;
  payrollEarnings: number;
  payrollDeductions: number;
  benefitsTotal: number;
  healthPlan: number;
  healthPromotion: number;
  foodBenefit: number;
  funeralAssistance: number;
  petPlan: number;
  primaryImportId?: string;
};

const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatCompetenceLabel = (competence: string): string => {
  const trimmed = competence?.trim();
  if (!trimmed || trimmed === 'Sem competência') return 'Sem competência';

  const normalized = trimmed.replace('/', '-');
  const [yearStr, monthStr] = normalized.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (Number.isInteger(year) && Number.isInteger(month) && month >= 1 && month <= 12) {
    const date = new Date(year, month - 1, 1, 12);
    const monthName = date.toLocaleString('pt-BR', { month: 'long' });
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
    return `${capitalizedMonth}/${year}`;
  }

  return trimmed;
};

export function ExpensesOverviewPage() {
  const location = useLocation();
  const { totals: benefitTotals, loading: benefitsLoading, error: benefitsError } = useBenefitCompetenceTotals();
  const { totals: payrollTotals, loading: payrollLoading, error: payrollError } = usePayrollCompetenceTotals();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isConsolidationModalOpen, setIsConsolidationModalOpen] = useState(false);
  const [selectedCompetence, setSelectedCompetence] = useState<{ raw: string; label: string } | null>(null);
  const [isConsolidating, setIsConsolidating] = useState(false);
  const [consolidationError, setConsolidationError] = useState<string | null>(null);
  const [consolidatedCompetences, setConsolidatedCompetences] = useState<Set<string>>(new Set());
  const [removingCompetence, setRemovingCompetence] = useState<string | null>(null);

  const benefitRoutes: Record<string, string> = {
    healthPlan: '/expenses/benefits/health-plan/imports',
    healthPromotion: '/expenses/benefits/health-promotion/imports',
    foodBenefit: '/expenses/benefits/food/imports',
    funeralAssistance: '/expenses/benefits/funeral-assistance/imports',
    petPlan: '/expenses/benefits/pet-plan/imports',
  };
  const backToParam = encodeURIComponent(`${location.pathname}${location.search}`);

  const overviewRows = useMemo<OverviewTotalsRow[]>(() => {
    const rowsMap = new Map<string, OverviewTotalsRow>();
    const getRow = (competence: string, sortKey: string | null): OverviewTotalsRow => {
      const label = competence || 'Sem competência';
      const existing = rowsMap.get(label);
      if (existing) {
        if (!existing.sortKey && sortKey) existing.sortKey = sortKey;
        return existing;
      }
      const next: OverviewTotalsRow = {
        competence: label,
        sortKey: sortKey || (competence ? competence : null),
        payrollTotal: 0,
        payrollEarnings: 0,
        payrollDeductions: 0,
        benefitsTotal: 0,
        healthPlan: 0,
        healthPromotion: 0,
        foodBenefit: 0,
        funeralAssistance: 0,
        petPlan: 0,
        primaryImportId: undefined,
      };
      rowsMap.set(label, next);
      return next;
    };

    payrollTotals.forEach((row) => {
      const target = getRow(row.competence, row.sortKey);
      target.payrollTotal += row.payrollTotal;
      target.payrollEarnings += row.earnings;
      target.payrollDeductions += row.deductions;
      if (!target.primaryImportId && row.primaryImportId) {
        target.primaryImportId = row.primaryImportId;
      }
    });

    benefitTotals.forEach((row) => {
      const target = getRow(row.competence, row.sortKey);
      target.benefitsTotal += row.total;
      target.healthPlan += row.healthPlan;
      target.healthPromotion += row.healthPromotion;
      target.foodBenefit += row.foodBenefit;
      target.funeralAssistance += row.funeralAssistance;
      target.petPlan += row.petPlan;
    });

    return Array.from(rowsMap.values()).sort((a, b) => {
      if (a.sortKey === b.sortKey) return 0;
      if (!a.sortKey) return 1;
      if (!b.sortKey) return -1;
      return b.sortKey.localeCompare(a.sortKey);
    });
  }, [benefitTotals, payrollTotals]);

  const totalPages = Math.max(1, Math.ceil(overviewRows.length / itemsPerPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [overviewRows.length]);

  useEffect(() => {
    setCurrentPage((prev) => (prev > totalPages ? totalPages : prev));
  }, [totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return overviewRows.slice(start, start + itemsPerPage);
  }, [overviewRows, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchConsolidated = async () => {
      try {
        const snap = await getDocs(collection(db, 'expenses'));
        setConsolidatedCompetences(new Set(snap.docs.map((doc) => doc.id)));
      } catch (error) {
        console.error('[ExpensesOverviewPage] Falha ao carregar consolidações', error);
      }
    };
    void fetchConsolidated();
  }, []);

  const handleItemsPerPageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value) || 10);
    setCurrentPage(1);
  };

  const handleOpenConsolidationModal = (competenceLabel: string, rawCompetence: string | null) => {
    const normalizedRaw = rawCompetence ?? (competenceLabel === 'Sem competência' ? 'unknown' : competenceLabel);
    setSelectedCompetence({
      raw: normalizedRaw,
      label: formatCompetenceLabel(competenceLabel),
    });
    setConsolidationError(null);
    setIsConsolidationModalOpen(true);
  };

  const handleCloseConsolidationModal = () => {
    setIsConsolidationModalOpen(false);
    setSelectedCompetence(null);
    setConsolidationError(null);
  };

  const handleConfirmConsolidation = async () => {
    if (!selectedCompetence?.raw) return;
    setIsConsolidating(true);
    setConsolidationError(null);
    try {
      await consolidateExpensesByCompetence(selectedCompetence.raw);
      setConsolidatedCompetences((prev) => {
        const next = new Set(prev);
        next.add(selectedCompetence.raw);
        return next;
      });
      toast.success('Despesas consolidadas com sucesso.');
      handleCloseConsolidationModal();
    } catch (error: any) {
      const message = error?.message ?? 'Não foi possível consolidar as despesas.';
      setConsolidationError(message);
    } finally {
      setIsConsolidating(false);
    }
  };

  const handleRemoveConsolidation = async (competenceKey: string, label: string) => {
    const confirmed = window.confirm(
      `Remover a consolidação de ${formatCompetenceLabel(label)}?\n\nIsso pode causar inconsistências em outros módulos.`,
    );
    if (!confirmed) return;
    setRemovingCompetence(competenceKey);
    try {
      await removeExpensesConsolidation(competenceKey);
      setConsolidatedCompetences((prev) => {
        const next = new Set(prev);
        next.delete(competenceKey);
        return next;
      });
      toast.success('Consolidação removida.');
    } catch (error: any) {
      const message = error?.message ?? 'Não foi possível remover a consolidação.';
      toast.error(message);
    } finally {
      setRemovingCompetence(null);
    }
  };

  const renderPayrollBreakdown = (row: OverviewTotalsRow) => {
    const hasEarnings = Boolean(row.payrollEarnings);
    const hasDeductions = Boolean(row.payrollDeductions);
    const hasTotal = Boolean(row.payrollTotal);
    if (!hasEarnings && !hasDeductions && !hasTotal) return '—';

    const content = (
      <div className="space-y-1">
        {hasEarnings ? (
          <div className="flex justify-between text-xs text-gray-600">
            <span>Proventos</span>
            <span className="font-medium text-gray-900">{formatCurrency(row.payrollEarnings)}</span>
          </div>
        ) : null}
        {hasDeductions ? (
          <div className="flex justify-between text-xs text-gray-600">
            <span>Deduções</span>
            <span className="font-medium text-gray-900">{formatCurrency(row.payrollDeductions)}</span>
          </div>
        ) : null}
        {hasTotal ? (
          <div className="flex justify-between text-xs text-gray-600">
            <span>Total</span>
            <span className="font-semibold text-gray-900">{formatCurrency(row.payrollTotal)}</span>
          </div>
        ) : null}
      </div>
    );

    if (!row.primaryImportId) return content;
    const href = `/expenses/payroll/${row.primaryImportId}?backTo=${backToParam}`;
    return (
      <Link
        to={href}
        className="block rounded-md border border-primary-100 px-3 py-2 text-primary-700 transition hover:border-primary-200 hover:bg-primary-50"
      >
        {content}
      </Link>
    );
  };

  const renderBenefitValue = (value: number, routeKey: keyof typeof benefitRoutes, row: OverviewTotalsRow) => {
    if (!value) return '—';
    if (!row.sortKey) return formatCurrency(value);
    const href = `${benefitRoutes[routeKey]}/${encodeURIComponent(row.sortKey)}?backTo=${backToParam}`;
    return (
      <Link to={href} className="text-primary-700 underline underline-offset-2 hover:text-primary-800">
        {formatCurrency(value)}
      </Link>
    );
  };

  const renderBenefitBreakdown = (row: OverviewTotalsRow) => {
    const items: Array<React.ReactNode> = [];

    const addItem = (value: number, label: string, routeKey: keyof typeof benefitRoutes) => {
      if (!value) return;
      const content = <span className="font-medium text-gray-900">{renderBenefitValue(value, routeKey, row)}</span>;
      items.push(
        <div key={label} className="flex justify-between text-xs text-gray-600">
          <span>{label}</span>
          {content}
        </div>
      );
    };

    addItem(row.healthPlan, 'Plano de saúde', 'healthPlan');
    addItem(row.healthPromotion, 'Promoção a saúde', 'healthPromotion');
    addItem(row.foodBenefit, 'Alimentação', 'foodBenefit');
    addItem(row.funeralAssistance, 'Auxílio funerário', 'funeralAssistance');
    addItem(row.petPlan, 'Plano pet', 'petPlan');

    const hasBenefits = items.length > 0 || Boolean(row.benefitsTotal);
    if (!hasBenefits) return '—';

    return (
      <div className="space-y-1">
        {items}
        <div className="flex justify-between border-t border-gray-100 pt-1 text-xs text-gray-700">
          <span>Total</span>
          <span className="font-semibold text-gray-900">{formatCurrency(row.benefitsTotal)}</span>
        </div>
      </div>
    );
  };

  const loadingTotals = benefitsLoading || payrollLoading;
  const totalsError = [benefitsError, payrollError].filter(Boolean).join(' ');

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <p className="text-sm font-semibold text-primary-700">Despesas</p>
        <h1 className="text-3xl font-bold text-gray-900">Visão geral</h1>
        <p className="max-w-2xl text-sm text-gray-600">
          Gerencie e acompanhe as despesas de folha de pagamento e benefícios da sua empresa.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Link
          to="/expenses/payroll"
          className="group flex h-full w-full flex-col justify-between gap-4 rounded-xl border border-primary-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Folha de pagamento</h2>
            <span className="rounded-full bg-primary-50 p-3 text-primary-600">
              <BanknotesIcon className="h-6 w-6" aria-hidden />
            </span>
          </div>
        </Link>
        <Link
          to="/expenses/charges"
          className="group flex h-full w-full flex-col justify-between gap-4 rounded-xl border border-primary-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Encargos</h2>
            <span className="rounded-full bg-primary-50 p-3 text-primary-600">
              <CalculatorIcon className="h-6 w-6" aria-hidden />
            </span>
          </div>
        </Link>
        {benefitTypes.map((benefit) => (
          <Link
            key={benefit.slug}
            to={`/expenses/benefits/${benefit.slug}`}
            className="group flex h-full w-full flex-col justify-between gap-4 rounded-xl border border-primary-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">{benefit.name}</h2>
              <span className="rounded-full bg-primary-50 p-3 text-primary-600">
                <benefit.icon className="h-6 w-6" aria-hidden />
              </span>
            </div>
          </Link>
        ))}
      </div>

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Competências consolidadas</h2>
            <p className="text-sm text-gray-600">
              Totais da folha e benefícios agregados por competência, incluindo detalhamento por tipo.
            </p>
          </div>
          {loadingTotals ? <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-500" aria-hidden /> : null}
        </div>

        {totalsError ? (
          <div className="mt-4 rounded-md border border-amber-100 bg-amber-50 p-4 text-sm text-amber-700">{totalsError}</div>
        ) : null}

        {!loadingTotals && overviewRows.length === 0 ? (
          <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            Nenhuma competência com dados de folha ou benefícios.
          </div>
        ) : null}

        {overviewRows.length > 0 ? (
          <>
            <div className="mt-4 overflow-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Competência</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Folha</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Benefícios</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Total geral</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {paginatedRows.map((row) => (
                    <tr key={row.competence}>
                      <td className="px-4 py-3 text-gray-900">
                        <div className="flex flex-col gap-1">
                          <span>{formatCompetenceLabel(row.competence)}</span>
                          {consolidatedCompetences.has(row.sortKey || row.competence) ? (
                            <span className="inline-flex w-fit items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-200">
                              Consolidado
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900 bg-blue-50">{renderPayrollBreakdown(row)}</td>
                      <td className="px-4 py-3 text-gray-900 bg-indigo-50">{renderBenefitBreakdown(row)}</td>
                      <td className="px-4 py-3 text-gray-900 font-semibold">
                        {formatCurrency(row.payrollTotal + row.benefitsTotal)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!consolidatedCompetences.has(row.sortKey || row.competence) ? (
                          <button
                            type="button"
                            onClick={() => handleOpenConsolidationModal(row.competence, row.sortKey)}
                            className="inline-flex items-center rounded-md border border-primary-200 px-3 py-1.5 text-sm font-semibold text-primary-700 transition hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                            disabled={isConsolidating}
                          >
                            Consolidar
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRemoveConsolidation(row.sortKey || row.competence, row.competence)}
                            className="inline-flex items-center rounded-md border border-rose-200 px-3 py-1.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-70"
                            disabled={removingCompetence === (row.sortKey || row.competence)}
                          >
                            {removingCompetence === (row.sortKey || row.competence) ? 'Removendo...' : 'Remover'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={overviewRows.length}
              goToPreviousPage={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              goToNextPage={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </>
        ) : null}
      </div>

      <BaseModal
        isOpen={isConsolidationModalOpen}
        onClose={handleCloseConsolidationModal}
        maxWidth="max-w-2xl"
        title={
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-900">Consolidar despesas</p>
            <p className="text-xs text-gray-600">Revise o impacto antes de confirmar.</p>
          </div>
        }
        actions={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
              onClick={handleCloseConsolidationModal}
              disabled={isConsolidating}
            >
              Fechar
            </button>
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-primary-400"
              onClick={handleConfirmConsolidation}
              disabled={!selectedCompetence || isConsolidating}
            >
              {isConsolidating ? (
                <>
                  <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Consolidando...
                </>
              ) : (
                'Confirmar consolidação'
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-3 py-4">
          <p className="text-sm text-gray-700">
            Ao consolidar as despesas de{' '}
            <span className="font-semibold text-gray-900">{selectedCompetence?.label ?? 'esta competência'}</span>, não será
            mais possível importar despesas para essa competência e as informações serão propagadas para os outros
            módulos.
          </p>
          <p className="text-sm text-gray-600">
            Use esta ação quando os lançamentos estiverem fechados e prontos para sincronização com os demais processos.
          </p>
          {consolidationError ? (
            <div className="rounded-md border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {consolidationError}
            </div>
          ) : null}
        </div>
      </BaseModal>
    </div>
  );
}
